import React from 'react';
import { AlertTriangle, Clock, Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const MaintenancePage = ({ settings }) => {
  const { language } = useLanguage();
  
  const getMessage = () => {
    switch (language) {
      case 'ar':
        return settings?.message_ar || "الموقع تحت الصيانة. سنعود قريباً.";
      case 'en':
        return settings?.message_en || "Site under maintenance. We'll be back soon.";
      default:
        return settings?.message_fr || "Site en maintenance. Nous serons bientôt de retour.";
    }
  };

  const getTitle = () => {
    switch (language) {
      case 'ar':
        return "الموقع تحت الصيانة";
      case 'en':
        return "Under Maintenance";
      default:
        return "Site en Maintenance";
    }
  };

  const getSubtitle = () => {
    switch (language) {
      case 'ar':
        return "نعمل على تحسين الموقع. شكراً لصبركم.";
      case 'en':
        return "We're working to improve the site. Thank you for your patience.";
      default:
        return "Nous travaillons à améliorer le site. Merci de votre patience.";
    }
  };

  const formatEndDate = () => {
    if (!settings?.end_date) return null;
    const date = new Date(settings.end_date);
    const options = { 
      day: 'numeric', 
      month: 'long', 
      hour: '2-digit', 
      minute: '2-digit' 
    };
    return date.toLocaleDateString(
      language === 'ar' ? 'ar-DZ' : language === 'en' ? 'en-US' : 'fr-FR', 
      options
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-lg w-full text-center">
        {/* Animated Icon */}
        <div className="relative mx-auto w-24 h-24 mb-8">
          <div className="absolute inset-0 bg-amber-500 rounded-full animate-ping opacity-20" />
          <div className="relative w-24 h-24 bg-amber-500 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-12 h-12 text-white" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-4xl font-bold text-white mb-4" dir={language === 'ar' ? 'rtl' : 'ltr'}>
          {getTitle()}
        </h1>

        {/* Message */}
        <p className="text-xl text-slate-300 mb-6" dir={language === 'ar' ? 'rtl' : 'ltr'}>
          {getMessage()}
        </p>

        <p className="text-slate-400 mb-8" dir={language === 'ar' ? 'rtl' : 'ltr'}>
          {getSubtitle()}
        </p>

        {/* End Date */}
        {settings?.end_date && (
          <div className="bg-slate-800/50 rounded-xl p-4 mb-8 inline-flex items-center gap-3" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <Clock className="w-5 h-5 text-amber-500" />
            <span className="text-slate-300">
              {language === 'ar' ? 'العودة المتوقعة:' : language === 'en' ? 'Expected return:' : 'Retour prévu :'}
              {' '}
              <strong className="text-white">{formatEndDate()}</strong>
            </span>
          </div>
        )}

        {/* Contact */}
        <div className="border-t border-slate-700 pt-6 mt-6">
          <p className="text-slate-500 text-sm mb-2" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {language === 'ar' ? 'للاستفسارات العاجلة:' : language === 'en' ? 'For urgent inquiries:' : 'Pour toute urgence :'}
          </p>
          <a 
            href="mailto:contact@sae.dz" 
            className="inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 transition-colors"
          >
            <Mail className="w-4 h-4" />
            contact@sae.dz
          </a>
        </div>

        {/* Logo placeholder */}
        <div className="mt-12 text-slate-600 text-sm">
          © SAE - Industrial & Agricultural Equipment
        </div>
      </div>
    </div>
  );
};

export default MaintenancePage;
